import { Experiment } from '@amplitude/experiment-js-client';
import type { ExperimentClient } from '@amplitude/experiment-js-client';
import { getConfig } from '@config';
import { readAnonymousIdFromCookie } from '@shared/core/featureFlags/remoteFeatureFlags/amplitude.utils';
import { addAction } from '@shared/utils/logger';

/**
 * Resolves the auth host for `authorize`, `changePassword` and logout from the
 * `auth-domain-joy-web-social-logout` Amplitude flag. The key still says "social" for historical
 * reasons; social login was moved out from under it and pinned to `withjoy.auth0.com`.
 *
 * This is a DEDICATED flag for joy-web's legacy `auth0.ts` flows — deliberately separate from the
 * `auth-domain-clients` flag that `/account-login` (`useAuthClient`) and carbon read, so ramping it
 * cannot affect those existing flows. Same `{ domain }` payload shape.
 *
 * Reads the flag via the Amplitude **Experiment client SDK** (the same mechanism `useAuthClient` and
 * carbon use), NOT the SSR-embedded `window.__INITIAL_AMPLITUDE_FLAGS__`. The SSR snapshot is only
 * written on guest-site routes, whereas these flows fire on `/account`, card, createWedding and
 * `/logout`; the SDK fetches client-side on every route, so it actually takes effect.
 *
 * Returns the flag's `payload.domain` verbatim, or `null` when unavailable — flag unset/malformed,
 * fetch error or timeout, or running on the server. Callers then fall back to this environment's auth
 * proxy (`getAuthDomain`), the same host every one of these flags is ramped to, so a flag-SDK gap lands
 * where the flag would have. The flag still wins whenever it evaluates, so it remains the kill switch.
 *
 * NOTE: login is not covered here. It uses auth0-js embedded auth (`/co/authenticate`), which the
 * proxy serves only for the client ids it allowlists, so it has its own key below. Social login is not
 * covered either: it is pinned to `withjoy.auth0.com` because the legacy app reads its session there,
 * and because a proxied call reaches Auth0 from our egress IP, which draws an MFA challenge a social
 * user cannot answer.
 *
 * Signup is the exception and has its own key below: `/dbconnections/signup` IS implemented by the
 * proxy, so it can move independently of login. See `auth0.ts`.
 *
 * Session refresh (`/delegation`, called from `token.service.ts`) also has its own key, so it can
 * ramp and roll back without moving any login flow.
 */
const SOCIAL_LOGOUT_FLAG_KEY = 'auth-domain-joy-web-social-logout';
const SIGNUP_FLAG_KEY = 'auth-domain-joy-web-signup';
const REFRESH_FLAG_KEY = 'auth-domain-joy-web-refresh';
const LOGIN_FLAG_KEY = 'auth-domain-joy-web-login';
const FETCH_TIMEOUT_MS = 2000;

// The only hosts a flag payload may select. The resolved host receives credentials: signup posts to
// `/dbconnections/signup`, changePassword to `/dbconnections/change_password`. The flag value is
// untrusted input, so anything off this list falls back to the caller's default rather than sending
// credentials to an arbitrary origin.
const ALLOWED_AUTH_DOMAINS = new Set<string>([
  'auth.withjoy.com', // proxy, prod/stage
  'auth-dev.withjoy.com', // proxy, dev/local
  'accounts.withjoy.com', // Auth0 custom domain
  'withjoy.auth0.com', // Auth0 tenant host
]);

// Distinct sentinel so the caller can tell "flag server timed out" apart from "fetch resolved but
// the flag isn't set" — both fall back to the env proxy, but they're different diagnostics.
const TIMEOUT = Symbol('auth-domain-fetch-timeout');

const raceWithTimeout = async <T>(promise: Promise<T>, ms: number): Promise<T | typeof TIMEOUT> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<typeof TIMEOUT>(resolve => {
    timer = setTimeout(() => resolve(TIMEOUT), ms);
  });
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timer !== undefined) {
      clearTimeout(timer);
    }
  }
};

type FetchResult = { client: ExperimentClient; timedOut: boolean } | null;

const fetchClient = async (): Promise<FetchResult> => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const config = getConfig();
    // `Experiment.initialize` caches ONE client per `instanceName + "." + apiKey` and ignores the
    // config of any later caller for that key. A dedicated `instanceName` keeps this a genuinely
    // separate client from the app's default-instance flag SDK (`useAmplitudeExperiment`, which uses
    // the same apiKey + default instance): without it, whichever initializes first wins and clobbers
    // the other's config (SSR initialVariants, exposure provider, automaticExposureTracking…) — an
    // app-wide flag-SDK regression — and the two share one fetch/user.
    const client: ExperimentClient = Experiment.initialize(config.amplitudeExperimentApiKey, {
      instanceName: 'auth-domain',
      serverUrl: config.amplitudeExperimentServer,
      flagsServerUrl: config.amplitudeExperimentFlagsServer,
      automaticExposureTracking: false,
    });
    // Bucket on the shared anonymous id (raw cookie, same reader SSR + the exposure tracker use).
    // This gives device-scoped consistency — it matches logged-out social login (the app also buckets
    // on device id there); changePassword/logout fire while logged in, where the app additionally
    // buckets on user id, so target these flags on device id (or accept device-scoped assignment).
    // NB: NOT the prefixing CookieStorage — that would look up `joy<name>` and never match.
    const deviceId = readAnonymousIdFromCookie();
    // Bounded so a slow/unreachable flag server can never block auth construction (`initAuth0`
    // queues every webAuth action until this settles). On timeout we read whatever the client has,
    // which yields `null` and falls back.
    const raced = await raceWithTimeout(client.fetch(deviceId ? { device_id: deviceId } : {}), FETCH_TIMEOUT_MS);
    return { client, timedOut: raced === TIMEOUT };
  } catch {
    return null;
  }
};

// One shared fetch — both flags are read off the same client, so we never double-fetch per page load.
let fetchPromise: Promise<FetchResult> | null = null;
const ensureFetched = (): Promise<FetchResult> => {
  if (!fetchPromise) {
    fetchPromise = fetchClient();
  }
  return fetchPromise;
};

const resolveDomainForFlag = async (flagKey: string): Promise<string | null> => {
  const result = await ensureFetched();
  if (!result) {
    addAction('authDomainResolved', { outcome: 'error', flag: flagKey });
    return null;
  }
  const variant = result.client.variant(flagKey);
  const domain = (variant?.payload as { domain?: unknown } | undefined)?.domain;
  if (typeof domain === 'string' && domain.length > 0) {
    if (!ALLOWED_AUTH_DOMAINS.has(domain)) {
      // Non-empty but not a known auth host — refuse it and fall back. Logged distinctly (hostname
      // only, no PII) so a misconfigured payload is visible rather than silently swallowed.
      addAction('authDomainResolved', { outcome: 'rejected', domain, flag: flagKey });
      return null;
    }
    // Fire-and-forget New Relic PageAction (no await, no PII — hostname only). Lets us confirm on
    // dev which host each load resolved to, incl. the redirect flows (logout) that never surface as
    // an AjaxRequest.
    addAction('authDomainResolved', { outcome: 'proxy', domain, flag: flagKey });
    return domain;
  }
  addAction('authDomainResolved', { outcome: result.timedOut ? 'timeout' : 'fallback', flag: flagKey });
  return null;
};

// `authDomainResolved` carries a `flag` attribute as of the signup split. It is additive, so an
// existing NRQL filter still matches, but a query that counts the action without faceting on `flag`
// sums every flag. Facet on it, or filter to one key.

// Per-flag memoization — each host is evaluated at most once per page load. All share the single
// `fetchPromise` above.
let socialLogoutPromise: Promise<string | null> | null = null;
let signupPromise: Promise<string | null> | null = null;
let loginPromise: Promise<string | null> | null = null;
let refreshPromise: Promise<string | null> | null = null;
// The refresh flag's answer once its lookup has finished; `undefined` until then.
let refreshResolved: string | null | undefined;

/**
 * The flag-resolved host for `authorize`, `changePassword` and logout (memoized), or `null` when the
 * flag is unavailable (caller falls back to this environment's auth proxy).
 */
export const resolveAuthDomainFromFlag = (): Promise<string | null> => {
  if (!socialLogoutPromise) {
    socialLogoutPromise = resolveDomainForFlag(SOCIAL_LOGOUT_FLAG_KEY);
  }
  return socialLogoutPromise;
};

/**
 * The flag-resolved host for signup (memoized), or `null` when unavailable (caller falls back to this
 * environment's auth proxy).
 *
 * Signup has its own key rather than sharing the one above: the two flows moved at different times,
 * and `auth-domain-clients` is kept as the no-deploy rollback for `/account-login` and carbon.
 */
export const resolveSignupAuthDomainFromFlag = (): Promise<string | null> => {
  if (!signupPromise) {
    signupPromise = resolveDomainForFlag(SIGNUP_FLAG_KEY);
  }
  return signupPromise;
};

/**
 * The flag-resolved host for password login (memoized), or `null` when unavailable (caller falls back
 * to this environment's auth proxy).
 */
export const resolveLoginAuthDomainFromFlag = (): Promise<string | null> => {
  if (!loginPromise) {
    loginPromise = resolveDomainForFlag(LOGIN_FLAG_KEY);
  }
  return loginPromise;
};

/**
 * The flag-resolved host for session refresh (`/delegation`), memoized, or `null` when unavailable
 * (the caller then uses this environment's auth proxy). Never rejects. Always `null` on the server.
 */
export const resolveRefreshAuthDomainFromFlag = (): Promise<string | null> => {
  if (!refreshPromise) {
    refreshPromise = resolveDomainForFlag(REFRESH_FLAG_KEY)
      .catch(() => null)
      .then(domain => {
        refreshResolved = domain;
        return domain;
      });
  }
  return refreshPromise;
};

/**
 * The refresh flag's host without waiting: the host or `null` once the lookup has finished,
 * `undefined` while it is still running (and starts it). Refresh must never wait on Amplitude.
 */
export const peekRefreshAuthDomain = (): string | null | undefined => {
  void resolveRefreshAuthDomainFromFlag();
  return refreshResolved;
};
